import type {
    DrugPathwayOptions,
    PathwayAlertConfig,
    PathwayOptionConfig,
    PathwayQuestionnaireConfig,
    ProgramTerms,
} from '../../shared';
import {
    INSURANCE_SELECTIONS_HEADING,
    PHARMACY_COVERAGE_PRICING_BULLET,
} from '../../shared-content';
import {
    MEDICARE_BRIDGE_ALERT_CONTENT,
    MEDICARE_BRIDGE_ALERT_CTA,
    ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE,
    ZEPBOUND_SELF_PAY_CARD_BASE,
    ZEPBOUND_TERMS_URL,
} from '../shared';

import { MEDICARE_BRIDGE_PRICE_NOTE, SELF_PAY_PRICE_NOTE } from './shared';

export const zepboundKwikpenPathwayAlerts: PathwayAlertConfig[] = [
    {
        pathways: ['fallback'],
        screens: ['coverage-select'],
        color: 'info',
        content: MEDICARE_BRIDGE_ALERT_CONTENT,
        cta: MEDICARE_BRIDGE_ALERT_CTA,
    },
    {
        pathways: ['self-pay'],
        screens: ['pharmacy-select'],
        color: 'info',
        content: MEDICARE_BRIDGE_ALERT_CONTENT,
        cta: MEDICARE_BRIDGE_ALERT_CTA,
    },
];

export const zepboundKwikpenTerms: ProgramTerms = {
    'self-pay': {
        heading: 'LillyDirect® Zepbound® KwikPen®',
        body: '',
        termsUrl: ZEPBOUND_TERMS_URL,
        incentiveProgram: {
            name: 'Zepbound® Self-Pay Journey Program',
            disclaimer:
                'You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® for an approved use consistent with FDA-approved product labeling.',
            terms: '',
        },
    },
};

// KwikPen bridge card adds priceLabel on top of the shared base.
const medicareBridgeCard: PathwayOptionConfig = {
    ...ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE,
    content: {
        ...ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE.content,
        priceLabel: 'Starting at',
        fallbackTermsUrl: ZEPBOUND_TERMS_URL,
        fallbackDisclaimer:
            'Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® or Zepbound® Single-Dose Vial for an approved use consistent with FDA-approved product labeling.',
    },
};

// KwikPen self-pay routes directly to pharmacy select.
const selfPayCard: PathwayOptionConfig = {
    ...ZEPBOUND_SELF_PAY_CARD_BASE,
    destination: 'pharmacy-select',
    content: {
        ...ZEPBOUND_SELF_PAY_CARD_BASE.content,
        priceSuperscript: '*',
        disclaimer:
            'Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® or Zepbound® Single-Dose Vial for an approved use consistent with FDA-approved product labeling.',
    },
};

export const zepboundKwikpenQuestionnaire: PathwayQuestionnaireConfig = {
    heading: 'How would you like to proceed?',
    description:
        'This helps us match you with the most relevant pharmacy and savings options.',
    options: [
        {
            id: 'self-pay',
            content: {
                heading: 'I want to self-pay',
                description: "I'll pay without using insurance",
            },
            target: { pathway: 'self-pay', destination: 'pharmacy-select' },
        },
        {
            id: 'medicare-bridge',
            content: {
                heading: 'I want to explore the Medicare GLP\u20111 Bridge',
                description:
                    'I have Medicare Part D and want to check my eligibility',
            },
            target: {
                pathway: 'government',
                subPathway: 'medicare-bridge',
                destination: 'pharmacy-select',
            },
        },
        {
            id: 'other-government',
            content: {
                heading: 'I have another type of government insurance',
                description:
                    'Such as TRICARE, Veterans Affairs, Indian Health Services, Bureau of Prisons, or Medicaid.',
            },
            target: { pathway: 'government', subPathway: 'other-government' },
        },
    ],
    notFoundText: 'Looking for other options?',
    notFoundModal: {
        heading: 'Not seeing what you expected?',
        body: 'If you have questions about your coverage, your healthcare provider is the best place to start. They can help you find the right option for your situation.',
    },
};

// Pharmacy-select screen cards - Medicare GLP\u20111 Bridge eligibility section
const pharmacyMedicareBridgeCard: PathwayOptionConfig = {
    id: 'medicare-bridge',
    fillPathway: 'government',
    headlinePriceType: 'MedicareBridge',
    content: {
        heading: 'Medicare GLP\u20111 Bridge',
        cardColor: 'success',
        headerIcon: 'security',
        showPrice: true,
        priceSuperscript: '*',
        priceNote: MEDICARE_BRIDGE_PRICE_NOTE,
        button: '',
    },
};

// Pharmacy-select screen cards - Medicare GLP\u20111 Bridge eligibility section
const pharmacySelfPayCard: PathwayOptionConfig = {
    id: 'self-pay',
    fillPathway: 'self-pay',
    headlinePriceType: 'CashAssistance',
    content: {
        heading: 'Self-pay starting at',
        cardColor: 'info',
        headerIcon: 'account-balance-wallet',
        showPrice: true,
        priceSuperscript: '‡',
        priceNote: SELF_PAY_PRICE_NOTE,
        button: '',
    },
};

// Shared government insurance card — also used by AutoInjector.
const governmentInsuranceCard: PathwayOptionConfig = {
    id: 'other-government',
    fillPathway: 'government',
    subPathway: 'other-government',
    destination: 'pharmacy-select',
    headlinePriceType: 'GovernmentPriceAtPharmacy',
    content: {
        heading: 'Government Insurance',
        headerIcon: 'security',
        noPriceText: 'Cost confirmed by your selected pharmacy',
        bullets: [
            PHARMACY_COVERAGE_PRICING_BULLET,
            {
                key: 'priorAuth',
                icon: 'warning',
                color: 'warning',
                text: '<b>May require prior authorization</b><br />Most plans require insurance approval which may take time.',
            },
        ],
        button: 'Use Government Insurance',
    },
};

export const zepboundKwikpenPathwayOptions: DrugPathwayOptions = {
    showEscapeHatch: true,
    default: { cards: [selfPayCard] },
    medicareBridge: { cards: [medicareBridgeCard, selfPayCard] },
    medicareBridgeContent: {
        heading:
            "Based on your insurance selections, you may have options. Here's what you can expect.",
        infoPanel: {
            icon: 'local-pharmacy',
            heading: 'Your pharmacy will verify your eligibility',
            description:
                "After you select a pharmacy, they'll verify your Medicare GLP\u20111 Bridge eligibility and, if eligible, help you with the prior authorization process. This is not a guarantee of coverage. Eligibility is determined through prior authorization.",
            outcomesLabel: 'TWO POSSIBLE OUTCOMES',
        },
        cards: [pharmacyMedicareBridgeCard, pharmacySelfPayCard],
    },
    governmentInsurance: {
        heading: INSURANCE_SELECTIONS_HEADING,
        description:
            'Certain plans including TRICARE, Veterans Affairs, Indian Health Services, and Bureau of Prisons may cover Zepbound® KwikPen®.<br><br>Coverage varies for other types of government insurance. You can check your coverage <a>here</a>, or use the self-pay option below.',
        descriptionLinkUrl: 'https://zepbound.lilly.com/insurance-check',
        cards: [governmentInsuranceCard, selfPayCard],
    },
};
